import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth0 } from "@auth0/auth0-react";
import { isMobile } from "react-device-detect";

import { getPuzzleFeedQueryKey, PUZZLE_FEED_PAGE_LIMIT } from "@/constants/puzzle-feed.constants";
import { getPuzzleFeed } from "@/services/PropertyService";
import PuzzleFeedCard from "./PuzzleFeedCard";
import AdSenseAd from "@/components/common/AdSenseAd/AdSenseAd";
import { PuzzleFeedItem } from "./types";
import config from "@/config";
import { usePuzzleFeedPageView } from "@/hooks/usePuzzleFeedPageView";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useUser } from "@/context/UserContext";
import { getUserGuess } from "@/services/UserService";
import { buildPuzzleShareDetails, buildPropertyShareLabel, hasSolvedPuzzle } from "@/helpers/puzzleShare";
import { buildPuzzleListingNavigationUrl } from "@/helpers/withCurrentSearchParams";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useShareScoreSheet } from "@/components/common/share-score-sheet/share-score-sheet-context";
import { getStreaks } from "@/services/UserService";
import { getDisplayPrice } from "@/helpers/helpers";
import { logger } from "@/services/LoggerService";
import SignUpCard from "@/components/common/SignUpCard/SignUpCard";
import { MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL } from "@/constants/mixpanel.constants";
import OneTapSignInDrawer from "@/components/common/OneTapSignIn/OneTapSignInDrawer";
import OneTapSignInModal from "@/components/common/OneTapSignIn/OneTapSignInModal";
import PageHead from "@/components/common/PageHead";
import { ONE_MINUTE } from "@/constants/time";
import useModalState from "@/hooks/useModalState";
import PuzzleFeedOnboardingCard from "./PuzzleFeedOnboardingCard";

const FEED_STATE_KEY = "puzzle-feed-state";
const PUZZLE_FEED_PAGE_VIEW_KEY = "page_view_tracked:/";
const PUZZLE_FEED_ONBOARDING_DISMISSED_KEY = "puzzle-feed-onboarding-dismissed";

const PuzzleFeed: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();
    const { userState, isLoading: isUserLoading } = useUser();
    const { setModalId } = useModalState();
    const requestingUserKey = userState?.id ? String(userState.id) : "anonymous";
    const [searchParams] = useSearchParams();
    const [feedState, setFeedState] = useLocalStorage<Record<string, any>>(FEED_STATE_KEY, {});
    const [guessedPropertyResults] = useLocalStorage<any[]>("guessed_property_results", []);
    const [visitorStatus] = useLocalStorage<string>("visitor-status", "");
    const [shareInFlightId, setShareInFlightId] = useState<string | null>(null);
    const pendingScrollRef = useRef<number | null>(null);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { openShareScoreSheet } = useShareScoreSheet();
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState<boolean>(false);
    const [showOnboardingCard, setShowOnboardingCard] = useState<boolean>(false);
    const queryClient = useQueryClient();

    const slc = searchParams.get("slc");
    const { syncFeedPageView } = usePuzzleFeedPageView();

    const getSessionStorageFlag = useCallback((key: string, context: string): string | null => {
        try {
            return sessionStorage.getItem(key);
        } catch (error) {
            logger.warn("Failed to read sessionStorage value", {
                context,
                key,
                error: error instanceof Error ? error.message : String(error),
            });
            return null;
        }
    }, []);

    const setSessionStorageFlag = useCallback((key: string, value: string, context: string): void => {
        try {
            sessionStorage.setItem(key, value);
        } catch (error) {
            logger.warn("Failed to write sessionStorage value", {
                context,
                key,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }, []);

    useEffect(() => {
        if (isUserLoading) return;
        const isRegisteredUser = isAuthenticated || Boolean(userState?.is_created);
        if (isRegisteredUser) {
            // TODO(PM-3754): Re-enable for registered users once we have a dedicated DB field.
            // Guests (and users who saw this as a guest right before registering) already saw the card anyway.
            // Do not use lastVisitedAt alone: App.tsx updates it on return visits (same session as login/nickname),
            // so registered users often already have it set before they reach this feed—onboarding would stay hidden.
            setShowOnboardingCard(false);
            return;
        }
        const dismissedForSession =
            getSessionStorageFlag(PUZZLE_FEED_ONBOARDING_DISMISSED_KEY, "PuzzleFeed.onboardingInit") === "true";
        setShowOnboardingCard(!dismissedForSession);
    }, [getSessionStorageFlag, isAuthenticated, isUserLoading, userState?.is_created]);

    const handleDismissOnboardingCard = useCallback(() => {
        sendMixpanelEvent("Onboarding Card Dismissed", {
            isAuthenticated,
            visitorStatus: isAuthenticated ? null : visitorStatus || "new-guest",
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            dismiss_method: "x_out",
        });
        setSessionStorageFlag(PUZZLE_FEED_ONBOARDING_DISMISSED_KEY, "true", "PuzzleFeed.dismissOnboarding");
        setShowOnboardingCard(false);
    }, [isAuthenticated, sendMixpanelEvent, setSessionStorageFlag, userState?.emailAddress, userState?.nickname, visitorStatus]);

    const handleOpenHelpModal = useCallback(() => {
        sendMixpanelEvent("Onboarding Card Dismissed", {
            isAuthenticated,
            visitorStatus: isAuthenticated ? null : visitorStatus || "new-guest",
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            dismiss_method: "how_to_play",
        });
        setSessionStorageFlag(PUZZLE_FEED_ONBOARDING_DISMISSED_KEY, "true", "PuzzleFeed.openHelpFromOnboarding");
        setShowOnboardingCard(false);
        setModalId("LIVE_VARIANT_HELP_MODAL");
    }, [
        isAuthenticated,
        sendMixpanelEvent,
        setModalId,
        setSessionStorageFlag,
        userState?.emailAddress,
        userState?.nickname,
        visitorStatus,
    ]);

    // Route-level Page View: one event per feed visit window.
    useEffect(() => {
        const path = window.location.pathname;
        // if (path !== "/puzzle") return;
        if (path !== "/" && path !== "/puzzle") return;

        const now = Date.now();
        let lastTs = 0;
        try {
            const lastTsRaw = sessionStorage.getItem(PUZZLE_FEED_PAGE_VIEW_KEY);
            const parsedTs = lastTsRaw != null ? Number(lastTsRaw) : 0;
            lastTs = Number.isFinite(parsedTs) ? parsedTs : 0;
        } catch (error) {
            logger.warn("Failed to read puzzle feed page view timestamp from sessionStorage", {
                context: "PuzzleFeed.pageViewThrottle",
                error: error instanceof Error ? error.message : String(error),
            });
            lastTs = 0;
        }

        if (now - lastTs < ONE_MINUTE) return;

        sendMixpanelEvent("Page View", {
            isAuthenticated,
            visitorStatus: isAuthenticated ? null : visitorStatus || "new-guest",
            pageName: "Property Feed - Puzzle",
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.emailAddress || null,
            pageUrl: window.location.href,
        });

        try {
            sessionStorage.setItem(PUZZLE_FEED_PAGE_VIEW_KEY, String(now));
        } catch (error) {
            logger.warn("Failed to write puzzle feed page view timestamp to sessionStorage", {
                context: "PuzzleFeed.pageViewThrottle",
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }, [isAuthenticated, sendMixpanelEvent, userState?.id, userState?.nickname, userState?.emailAddress, visitorStatus]);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteQuery({
        queryKey: [
            ...getPuzzleFeedQueryKey(
                userState?.id ? String(userState.id) : undefined,
                slc,
            ),
            userState.id,
            slc,
        ],
        queryFn: ({ pageParam = 1 }) =>
            getPuzzleFeed({
                page: pageParam as number,
                limit: PUZZLE_FEED_PAGE_LIMIT,
                userId: userState?.id,
                requestingUserId: userState?.id,
                slc: pageParam === 1 ? slc : null,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage: any, allPages) => {
            if (lastPage?.data?.hasNextPage) {
                return allPages.length + 1;
            }
            return undefined;
        },
    });

    const feedItems: PuzzleFeedItem[] = useMemo(
        () => data?.pages?.flatMap((page: any) => page?.data?.data ?? []) ?? [],
        [data?.pages],
    );

    const loadedFeedPageCount = data?.pages?.length ?? 0;

    useEffect(() => {
        syncFeedPageView(Math.max(1, loadedFeedPageCount));
    }, [loadedFeedPageCount, syncFeedPageView]);
    const refreshPreviewIds = useMemo(() => feedItems.slice(0, 8).map((item) => String(item.id)), [feedItems]);

    // iOS Safari can restore puzzle feed from bfcache without remounting.
    // Refresh only a small, likely-visible subset to avoid refetch bursts across the full feed.
    useEffect(() => {
        const refreshCommentPreviews = () => {
            if (refreshPreviewIds.length === 0) return;
            refreshPreviewIds.forEach((playablePropertyId) => {
                void queryClient.invalidateQueries({
                    queryKey: ["puzzle-feed-comments-preview", playablePropertyId, requestingUserKey],
                    exact: true,
                    refetchType: "active",
                });
            });
        };

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                refreshCommentPreviews();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                refreshCommentPreviews();
            }
        };

        window.addEventListener("pageshow", handlePageShow);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("pageshow", handlePageShow);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [queryClient, refreshPreviewIds, requestingUserKey]);

    // Prefix sums for stable cross-page indexing even when page sizes vary.
    const pageStartOffsets = useMemo(() => {
        const pages = data?.pages ?? [];
        let running = 0;
        return pages.map((page: any) => {
            const offset = running;
            running += (page?.data?.data ?? []).length;
            return offset;
        });
    }, [data?.pages]);

    // Controls how often and where within each interval the sign-up card appears in the feed.
    const SIGNUP_CARD_INTERVAL = 10;
    const SIGNUP_CARD_POSITION = 2; // zero-based index within each interval

    useEffect(() => {
        if (error) {
            console.error(error);
            logger.error("Error loading puzzle feed", error, {
                context: "PuzzleFeed",
                userId: userState?.id,
            });
            toast.error("Unable to load puzzles right now. Please try again later.");
        }
    }, [error, userState?.id]);

    // Show one-tap prompt on feed load for guests (once per session, shared across pages)
    useEffect(() => {
        if (isAuthenticated || userState?.is_created) {
            setShowGuestAuthPrompt(false);
            localStorage.removeItem("puzzle-one-tap-active");
            return;
        }

        const shown = getSessionStorageFlag("puzzle-one-tap-shown", "PuzzleFeed.oneTapPromptInit");
        const isActive = localStorage.getItem("puzzle-one-tap-active") === "true";

        // If another page has already activated the one-tap prompt in this browser
        // session, keep it active here without re-triggering the tracking flags.
        if (isActive) {
            setShowGuestAuthPrompt(true);
            return;
        }

        if (!shown) {
            setShowGuestAuthPrompt(true);
            setSessionStorageFlag("puzzle-one-tap-shown", "true", "PuzzleFeed.oneTapPromptInit");
            localStorage.setItem("puzzle-one-tap-active", "true");
        }
    }, [getSessionStorageFlag, isAuthenticated, setSessionStorageFlag, userState?.is_created]);

    const closeGuestPrompt = useCallback(() => {
        setShowGuestAuthPrompt(false);
        localStorage.removeItem("puzzle-one-tap-active");
    }, []);

    useEffect(() => {
        if (typeof feedState?.lastScrollPosition === "number") {
            pendingScrollRef.current = feedState.lastScrollPosition;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (pendingScrollRef.current !== null && feedItems.length > 0) {
            const scrollTop = pendingScrollRef.current;
            pendingScrollRef.current = null;
            requestAnimationFrame(() => {
                window.scrollTo({ top: scrollTop });
            });
        }
    }, [feedItems.length]);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    setFeedState((prev) => ({
                        ...(prev || {}),
                        lastScrollPosition: window.scrollY,
                    }));
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [setFeedState]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 },
        );
        const current = loaderRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const deriveStatus = useCallback(
        (item: PuzzleFeedItem): "won" | "lost" | "unplayed" => {
            if (userState?.id) {
                // For authenticated users, use userGuessCount to determine if guessed
                if (item.userGuessCount && item.userGuessCount > 0) {
                    if (item.userGuessCount <= 6) {
                        return "won";
                    }
                    return "lost";
                }
                return "unplayed";
            }
            // For unauthenticated users, check localStorage
            const cached = guessedPropertyResults?.find(
                (result) => result?.playablePropertyId === item.id && result?.version === "v5",
            );
            if (!cached) return "unplayed";
            const actualPrice = getDisplayPrice(cached.property ?? item.property);
            const solved = hasSolvedPuzzle(cached.guesses ?? [], Number(actualPrice ?? 0));
            return solved ? "won" : "lost";
        },
        [guessedPropertyResults, userState?.id],
    );

    const getSolvedGuessCount = useCallback(
        (item: PuzzleFeedItem): number | null => {
            // For authenticated users, use userGuessCount from the item
            if (userState?.id) {
                return item.userGuessCount && item.userGuessCount > 0 ? item.userGuessCount : null;
            }
            // For guest users, use the solvedGuessCount saved when puzzle was completed
            const cached = guessedPropertyResults?.find(
                (result) => result?.playablePropertyId === item.id && result?.version === "v5",
            );
            if (!cached?.guesses) return null;
            const actualPrice = Number(getDisplayPrice(cached.property ?? item.property));
            if (!actualPrice) return null;
            const correctIndex = cached.guesses.findIndex((guess: any) => Number(guess) === actualPrice);
            return correctIndex !== -1 ? correctIndex + 1 : null;
        },
        [guessedPropertyResults, userState?.id],
    );

    const handleNavigateToPuzzle = (item: PuzzleFeedItem) => {
        // Save scroll position synchronously to localStorage before navigation
        const currentState = feedState || {};
        const newState = {
            ...currentState,
            lastScrollPosition: window.scrollY,
        };
        try {
            localStorage.setItem("puzzle-feed-state", JSON.stringify(newState));
            // Also set a flag in sessionStorage as a backup
            sessionStorage.setItem("from-puzzle-feed", "true");
        } catch (error) {
            console.warn("Failed to save feed state:", error);
            logger.warn("Failed to save feed state", {
                context: "PuzzleFeed",
                error: String(error),
            });
        }
        setFeedState(newState);

        if (item.shortLink) {
            navigate(buildPuzzleListingNavigationUrl(`/puzzle/${item.shortLink}`));
        } else if (item.longLink) {
            navigate(buildPuzzleListingNavigationUrl(`/puzzle${item.longLink}`));
        }
    };

    const fetchGuessesForShare = async (item: PuzzleFeedItem) => {
        const cached = guessedPropertyResults?.find(
            (result) => result?.playablePropertyId === item.id && result?.version === "v5",
        );

        if (!userState?.id && cached?.guesses?.length) {
            return cached.guesses;
        }

        if (!userState?.id) {
            return null;
        }

        const response = await getUserGuess(
            userState.id as any,
            item.property.id,
            null as any,
            null as any,
            item.id as any,
            "puzzle",
        );

        return response?.guess?.map((entry: any) => String(entry?.guess).padStart(8, "0")) ?? null;
    };

    const handleShareScore = async (item: PuzzleFeedItem, status: "won" | "lost" | "unplayed") => {
        if (shareInFlightId) return;
        if (!item.shortLink) {
            toast.error("Missing data needed to share this puzzle.");
            return;
        }

        setShareInFlightId(item.id);
        try {
            const propertyLabel = buildPropertyShareLabel(item.property);
            const simpleShareTitle = `PriceMe: ${propertyLabel}`;
            const simpleShareUrl = `${window.location.origin}/puzzle/${item.shortLink}`;

            let shareTextTitle = simpleShareTitle;
            let shareTextBody = "";
            let shareUrl = simpleShareUrl;
            let userScore = 0;

            if (status !== "unplayed") {
                const guesses = await fetchGuessesForShare(item);
                if (guesses && guesses.length > 0) {
                    const shareDetails = buildPuzzleShareDetails({
                        guesses,
                        actualPrice: Number(getDisplayPrice(item.property)),
                        playDate: item.playDate,
                        shortLink: item.shortLink,
                        propertyLabel,
                    });
                    shareTextTitle = shareDetails.shareTextTitle;
                    shareTextBody = shareDetails.shareTextBody;
                    shareUrl = shareDetails.shareUrl;

                    if (status === "won") {
                        if (item.userGuessCount && item.userGuessCount > 0) {
                            userScore = item.userGuessCount;
                        } else {
                            // Find the index of the correct guess by comparing to actual price
                            const actualPrice = Number(getDisplayPrice(item.property));
                            if (actualPrice) {
                                const correctIndex = guesses.findIndex((guess: any) => {
                                    const guessValue = Number(guess);
                                    return guessValue === actualPrice;
                                });
                                if (correctIndex !== -1) {
                                    userScore = correctIndex + 1;
                                }
                            }
                        }
                    }
                }
            }

            let streak: number | null = null;
            if (isAuthenticated && userState?.id) {
                try {
                    const streakResponse = await getStreaks(String(userState.id));
                    streak = streakResponse?.data?.streaks?.count ?? null;
                } catch {
                    streak = null;
                }
            }
            openShareScoreSheet({
                shareTextTitle,
                shareTextBody,
                shareUrl,
                streak,
                location: "Puzzle Feed",
                pageName: "Property Feed - Puzzle",
                propertyUrl: item.shortLink ? `${window.location.origin}/puzzle/${item.shortLink}` : null,
                userScore,
                enableShareWithLink: status !== "unplayed",
            });
        } catch (shareError) {
            console.error(shareError);
            logger.error("Error sharing puzzle score", shareError, {
                context: "PuzzleFeed",
                itemId: item.id,
                userId: userState?.id,
            });
            toast.error("Unable to share score right now.");
        } finally {
            setShareInFlightId(null);
        }
    };

    return (
        <div className="puzzle-feed priceme-page-with-sticky-footer">
            <PageHead
                title="PriceMe | Feed"
                description="Scroll through spectacular homes and guess the prices"
                canonicalUrl={`${config.BASE_URL}/`}
            />
            {/*
            <PageHead
                title="PriceMe Puzzle | The home price guessing game"
                description="Play the daily home price puzzle. Guess the price in 6 tries or less!"
            />
            */}
            {/* One-tap overlays are fixed-position; they don't need to be inside the feed layout.
                Keep them mounted so the internal `show` prop can control open/close animations. */}
            {isMobile ? (
                <OneTapSignInDrawer
                    show={showGuestAuthPrompt}
                    onClose={closeGuestPrompt}
                    redirectPath="/"
                    version="puzzle"
                    location={MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL}
                />
            ) : (
                <OneTapSignInModal
                    show={showGuestAuthPrompt}
                    onClose={closeGuestPrompt}
                    redirectPath="/"
                    version="puzzle"
                    location={MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL}
                />
            )}

            <div className="tw-flex tw-flex-row tw-w-full tw-justify-center tw-gap-4">
                {/* Left Sidebar Ad - Desktop Only */}
                {!isMobile && (
                    <div className="tw-flex-shrink-0 tw-w-[300px] tw-max-w-[385px] tw-pl-4">
                        <div className="tw-sticky tw-top-[calc(9svh+2rem)] tw-w-full tw-z-10">
                            {/* <AdSenseAd
                                slot={config.ADSENSE_PUZZLE_SIDEBAR_SLOT_ID}
                                format="vertical"
                            /> */}
                        </div>
                    </div>
                )}

                {/* Main Feed Content */}
                <div className="puzzle-feed__container tw-w-full md:tw-max-w-[728px] tw-mx-auto tw-pt-2 md:tw-px-0">
                    {isLoading && feedItems.length === 0 ? (
                        <div className="puzzle-feed__loader tw-flex tw-w-full tw-justify-center tw-items-center tw-py-6">
                            <div className="tw-text-gray-600">Loading puzzles...</div>
                        </div>
                    ) : (
                        <>
                            {showOnboardingCard && (
                                <PuzzleFeedOnboardingCard
                                    onClose={handleDismissOnboardingCard}
                                    onOpenHelp={handleOpenHelpModal}
                                />
                            )}
                            {(data?.pages ?? []).map((page: any, pageIndex: number) =>
                                (page?.data?.data ?? []).map((item: PuzzleFeedItem, itemIndex: number) => {
                                    const globalIndex = (pageStartOffsets[pageIndex] ?? 0) + itemIndex;
                                    return (
                                        <React.Fragment key={`${item.id}-${pageIndex}-${itemIndex}`}>
                                            {!isAuthenticated &&
                                                !userState?.is_created &&
                                                globalIndex % SIGNUP_CARD_INTERVAL === SIGNUP_CARD_POSITION && (
                                                    <SignUpCard redirectPath="/" version="puzzle" /> /* was redirectPath="/puzzle" */
                                                )}
                                            <PuzzleFeedCard
                                                item={item}
                                                status={deriveStatus(item)}
                                                solvedGuessCount={getSolvedGuessCount(item)}
                                                onNavigate={() => handleNavigateToPuzzle(item)}
                                                onShare={(shareStatus) => handleShareScore(item, shareStatus)}
                                                isShareLoading={shareInFlightId === item.id}
                                            />
                                            {/* Insert one ad at the 4th content card of each fetched page batch. */}
                                            {itemIndex === 3 && (
                                                <AdSenseAd
                                                    key={`puzzle-feed-ad-${pageIndex}-${itemIndex}`}
                                                    slot={config.ADSENSE_PUZZLE_FEED_SLOT_ID}
                                                    format="fluid"
                                                    adLayoutKey={config.ADSENSE_IN_FEED_LAYOUT_KEY}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                }),
                            )}
                            <div
                                ref={loaderRef}
                                className="puzzle-feed__loader tw-flex tw-w-full tw-justify-center tw-items-center tw-py-6"
                            >
                                {hasNextPage ? (
                                    <span className="tw-text-gray-600 tw-text-sm">
                                        {isFetchingNextPage ? "Loading more puzzles..." : "Scroll for more"}
                                    </span>
                                ) : (
                                    <span className="tw-text-gray-600 tw-text-sm">You’ve reached the beginning.</span>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Right Sidebar Ad - Desktop Only */}
                {!isMobile && (
                    <div className="tw-flex-shrink-0 tw-w-[320px] tw-min-w-[320px] tw-max-w-[385px] tw-pr-4">
                        <div className="tw-sticky tw-top-[calc(9svh+2rem)] tw-w-full tw-z-10">
                            <AdSenseAd
                                slot={config.ADSENSE_PUZZLE_SIDEBAR_SLOT_ID}
                                format="vertical"
                                placementType="sticky-sidebar"
                            />
                        </div>
                    </div>
                )}
            </div>
            <AdSenseAd placementType="sticky-footer" />
        </div>
    );
};

export default PuzzleFeed;
